import React, { useState } from 'react';
import styles from './ccf-app-header.module.scss';
import Badge from '@mui/material/Badge';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  getApplicationLocale,
  getCopilotConfig,
} from '@nice-ccf/ui-components';
import {
  AppBar,
  Toolbar,
  IconButton,
  InputBase,
  Theme,
  Grid,
  alpha,
  Link,
  useTheme,
  Divider,
  Box,
  Select,
  MenuItem,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import SearchIcon from '@mui/icons-material/Search';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { ToastContainer } from 'react-toastify';
import AppsIcon from '@mui/icons-material/Apps';
import CcfAgentState from '../ccf-agent-state/ccf-agent-state';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import {
  CcfBox,
  CcfCxoneIcon,
  CcfCxoneLogoIcon,
  CcfHelpIcon,
  CcfNavigateToSimulatorIcon,
  CcfReloadIcon,
  CcfTooltip,
  CcfTypography,
  useTranslator,
} from '@nice-ccf/ui-controls';
import {
  getPartnerAccessTabVisibilityState,
  getSimulatorTabVisibilityState,
  globalActions,
} from '../global.app.slice';
import { useDispatch, useSelector } from 'react-redux';
import CcfAppSpaceStyles from './ccf-app-header-styles';
import CcfAgentLeg from '../ccf-agent-leg/ccf-agent-leg';
import {
  getLastContacts,
  setJsonFileUploadStatus,
  useSimulatorToInsertContact,
} from '../ccf-inbox/ccf-inbox.slice';
import { authorizePartnersAccess } from '../login/mock-data/authorizePartnersAccess';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { selectLoginCredentialsState } from '../login/+state/login.slice';
import { useAdminAccess } from '../ccf-app-space/ccf-copilot/useAdminAccess';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    flexGrow: 1,
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  title: {
    flexGrow: 1,
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'block',
    },
  },
  search: {
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${alpha(theme.palette.common.white, 0.5)}`,
    marginLeft: 0,
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1),
      width: 'auto',
    },
  },
  searchIcon: {
    padding: theme.spacing(0, 1),
    color: alpha(theme.palette.common.white, 0.5),
    height: '50%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  appHeaderDivider: {
    height: '60%',
    backgroundColor: '#fff',
    opacity: '0.2',
    borderBottomWidth: '40px',
  },
  inputRoot: {
    color: 'inherit',
  },
  inputInput: {
    color: 'white',
    padding: theme.spacing(0.5, 1, 0.5, 0),
    paddingLeft: `calc(1em + ${theme.spacing(3)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '120px',
      '&:focus': {
        width: '120px',
      },
    },
  },
}));

const supportedLanguages = [
  { code: 'en-US', label: 'English' },
  { code: 'pt-BR', label: 'Português' },
];

/* eslint-disable-next-line */
export interface CcfAppHeaderProps {}

/**
 * Component displays App header on landing screen
 * @param props -CcfAppHeaderProps
 * @returns app header component for landing screen
 * @example <CcfAppHeader/>
 */
export function CcfAppHeader(props: CcfAppHeaderProps) {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [count, setCount] = useState(2);
  const isSimulatorTabVisible = useSelector(getSimulatorTabVisibilityState);
  const isPartnerAccessTabVisible = useSelector(
    getPartnerAccessTabVisibilityState
  );
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const loginCredentials = useSelector(selectLoginCredentialsState);
  const loginCredentialsFromSSO = accounts[0]?.name || ' ';
  const loginCredentialsArray = loginCredentialsFromSSO?.split(' ');
  const assignName = isAuthenticated
    ? `${
        loginCredentialsArray[0].charAt(0).toUpperCase() +
        loginCredentialsArray[0].slice(1).toLowerCase()
      } ${
        loginCredentialsArray[1].charAt(0).toUpperCase() +
        loginCredentialsArray[1].slice(1).toLowerCase()
      }`
    : loginCredentials?.firstName + ' ' + loginCredentials?.lastName;
  const { setShowPartnerAccessPage } = globalActions;
  const url = window.location.host;
  const appHeaderStyles = CcfAppSpaceStyles(theme);
  const lastContact = useSelector(getLastContacts);
  const locale = useSelector(getApplicationLocale);
  const { setLocale } = globalActions;
  const [translate] = useTranslator();
  const coPilotConfigurations = useSelector(getCopilotConfig);
  // eslint-disable-next-line @nice-cxone/ccf/required-tsdoc
  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    const selectedLanguage = event.target.value;
    dispatch(setLocale(selectedLanguage));
  };
  const { isAuthorized } = useAdminAccess();
  /**
   *
   * @example
   */
  function navigateToSimulator() {
    if (url === 'localhost:4200') {
      window.open('/simulator');
    } else {
      const urlPath = 'https://' + window.location.hostname + '/simulator';
      window.open(urlPath);
    }
  }

  /**
   *
   * @example
   */
  function navigateToPartnerAccess() {
    dispatch(setShowPartnerAccessPage(true));
  }

  return (
    <AppBar position="static" className={styles.header}>
      <Grid container>
        <Grid item lg={5} md={5} sm={5} xs={5}></Grid>
        <Grid item lg={1} md={1} sm={1} xs={1}>
          <CcfBox className="cxoneLogo">
            <CcfCxoneLogoIcon style={{ width: '109px', height: '40px' }} />
          </CcfBox>
        </Grid>

        <Grid item lg={6} md={6} sm={6} xs={6}>
          <Toolbar className="toolbar">
          {isAuthorized && (
              <div className="adminblock">
                <AdminPanelSettingsIcon
                  style={{ cursor: 'pointer', marginRight: '10px' }}
                  onClick={() => window.open('/admin')}
                />
              </div>
            )}
            {isPartnerAccessTabVisible &&
              authorizePartnersAccess.find(
                (x: any) => assignName && x === assignName
              ) && (
                <div
                  id="partnerAccessIcon"
                  className="simulatorBlock"
                  onClick={() => navigateToPartnerAccess()}
                >
                  <AdminPanelSettingsIcon style={{ fontSize: '1.7rem' }} />
                </div>
              )}
            <Divider
              orientation={'vertical'}
              variant={'fullWidth'}
              className={classes.appHeaderDivider}
              sx={{ borderColor: '#fff !important' }}
            />
            <div
              id="simulatorIcon"
              className="simulatorBlock"
              onClick={() => navigateToSimulator()}
            >
              {isSimulatorTabVisible && (
                <CcfNavigateToSimulatorIcon
                  style={{ marginTop: '5px', fontSize: '1.7rem' }}
                />
              )}
            </div>
            <Divider
              orientation={'vertical'}
              variant={'fullWidth'}
              className={classes.appHeaderDivider}
              sx={{ borderColor: '#fff !important' }}
            />
            {`${[
              lastContact[0]?.customer?.firstName,
              lastContact[0]?.customer?.middleName,
              lastContact[0]?.customer?.lastName,
            ]
              .filter(Boolean)
              .join(' ')}` &&
              lastContact[0]?.copilotSource === 'Live LLM' && (
                <>
                  <div
                    className="simulatorBlock"
                    style={{ height: '22px', width: '50px' }}
                    onClick={() => {
                      const currentDate = new Date();
                      const dd = String(currentDate.getDate()).padStart(2, '0');
                      const mm = String(currentDate.getMonth() + 1).padStart(
                        2,
                        '0'
                      );
                      const hh = String(currentDate.getHours()).padStart(
                        2,
                        '0'
                      );
                      const MM = String(currentDate.getMinutes()).padStart(
                        2,
                        '0'
                      );
                      const ss = String(currentDate.getSeconds()).padStart(
                        2,
                        '0'
                      );
                      const generatedNumber = parseInt(dd + mm + hh + MM + ss);
                      const id = generatedNumber.toString();
                      if (
                        lastContact[0]?.customerChatReplyJsonData?.length > 0
                      ) {
                        dispatch(setJsonFileUploadStatus(true));
                      }
                      // eslint-disable-next-line react-hooks/rules-of-hooks
                      dispatch(useSimulatorToInsertContact({...lastContact[0], id: id}));
                    }}
                  >
                    <CcfTooltip title={translate('refresh')} arrow>
                      <div>
                        <CcfReloadIcon htmlColor={'#fff'} viewBox="0 0 27 27" />
                      </div>
                    </CcfTooltip>
                  </div>
                  <Divider
                    orientation={'vertical'}
                    variant={'fullWidth'}
                    className={classes.appHeaderDivider}
                    sx={{ borderColor: '#fff !important' }}
                  />
                </>
              )}
            <CcfTooltip title={translate('help')} arrow>
              <Link
                underline="none"
                target="_blank"
                rel="noopener noreferrer"
                href={''}
                sx={[appHeaderStyles.button, appHeaderStyles.helpIcon]}
              >
                <CcfHelpIcon htmlColor={'#fff'} viewBox="0 0 24 24" />
              </Link>
            </CcfTooltip>
            <Divider
              orientation={'vertical'}
              variant={'fullWidth'}
              className={classes.appHeaderDivider}
              sx={{ borderColor: '#fff !important' }}
            />
            <CcfTooltip title={translate('notificationBadge')} arrow>
              <Box
                color="inherit"
                role="article"
                aria-label={'notifications'}
                sx={{ padding: '12px' }}
                data-testid="notifications"
              >
                <Badge
                  badgeContent={count}
                  classes={{
                    badge: 'notificationBadge',
                  }}
                >
                  <NotificationsNoneIcon
                    htmlColor="#fff"
                    className="notificationsOutlinedIcon"
                  />
                </Badge>
              </Box>
            </CcfTooltip>
            <CcfAgentLeg />
            <div
              color="inherit"
              aria-label="agent state"
              className="userProfile"
            >
              <CcfAgentState agentStateForEmbeededView={false}></CcfAgentState>
            </div>
            {/* <Divider                  // not used by Eternals
              orientation={'vertical'}
              variant={'fullWidth'}
              className={classes.appHeaderDivider}
              sx={{ borderColor: '#fff !important' }}
            />
            <Select
              value={coPilotConfigurations?.language}
              onChange={handleLanguageChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Select language', readOnly: true }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 48 * 10,
                    overflow: 'auto',
                  },
                },
              }}
              sx={{
                color: 'white',
                marginRight: '20px',
                '& .MuiInputBase-input': {
                  border: 'none',
                },
                '&:before, &:after': {
                  border: 'none',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&:hover:not(.Mui-disabled):before': {
                  border: 'none',
                },
                '& .MuiSelect-icon': {
                  display: 'none',
                },
              }}
            >
              {supportedLanguages.map((language) => (
                <MenuItem key={language.code} value={language.code}>
                  {language.label}
                </MenuItem>
              ))}
            </Select> */}
          </Toolbar>
        </Grid>
      </Grid>
      <ToastContainer
        enableMultiContainer
        containerId={'AgentMessageToastContainer'}
        position="top-right"
        newestOnTop={false}
        closeButton={false}
        closeOnClick={false}
        rtl={false}
      />
    </AppBar>
  );
}

export default CcfAppHeader;
